/**
 * atomberg-variant-selector.js
 * ────────────────────────────
 * Shared variant-selection toolkit consumed by atomberg-product-card.js and the
 * PDP variant-picker block. Centralizes:
 *   - Exact and nearest-available variant lookup
 *   - Reading current option selections from a DOM scope
 *   - Recomputing per-value availability (strike-through state)
 *   - Price formatting and discount percent math
 *
 * Conventions consumed by the DOM helpers:
 *   - Each option group: element with `[data-option-index]` (0-based index)
 *   - Each option button: element with `[data-option-value]` inside the group
 *   - Active state: any class matching `*--active` on the option button
 *   - Unavailable state: any class matching `*--unavailable` on the option button
 *
 * Exposes itself once via `window.AtombergVariantSelector`.
 */
(function () {
  /** @type {any} */
  var w = window;
  if (w._atombergVariantSelectorInit) return;
  w._atombergVariantSelectorInit = true;

  /**
   * @typedef {Object} ProductVariant
   * @property {number} id
   * @property {string|null} option1
   * @property {string|null} option2
   * @property {string|null} option3
   * @property {number} price
   * @property {number|null} [compare_at_price]
   * @property {boolean} available
   * @property {{src: string, alt?: string}|null} [featured_image]
   * @property {{src: string, alt?: string}|null} [image]
   */

  /**
   * Finds the first variant whose options match the requested values. A null/empty
   * slot in `opts` is treated as a wildcard for that position.
   *
   * @param {Array<ProductVariant>} variants
   * @param {Array<string|null>} opts
   * @returns {ProductVariant | null}
   */
  function findVariant(variants, opts) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (
        (!opts[0] || v.option1 === opts[0]) &&
        (!opts[1] || v.option2 === opts[1]) &&
        (!opts[2] || v.option3 === opts[2])
      ) {
        return v;
      }
    }
    return null;
  }

  /**
   * Returns the best variant after a single option change: prefers an exact
   * match (every currently-selected option preserved, just the clicked one
   * swapped in) that is available; then that same exact match even if it's
   * sold out — a real SKU for the full combination is a truer match than any
   * other combination that merely shares the clicked value, so e.g. clicking
   * a color while Sweep Size + Fan Type are selected must never silently
   * drop those and jump to color's "first available" combo instead (wrong
   * price, wrong image, wrong everything but the color). Only when NO
   * variant exists at all for the full combination do we fall back to any
   * variant sharing just the changed value — available first, then even
   * sold out, so clicking a fully out-of-stock value still selects it
   * (showing the Sold out state) instead of being a dead click.
   *
   * @param {Array<ProductVariant>} variants
   * @param {Array<string|null>} opts
   * @param {number} changedIdx
   * @param {string} changedValue
   * @returns {ProductVariant | null}
   */
  function findBestAvailable(variants, opts, changedIdx, changedValue) {
    var test = opts.slice();
    test[changedIdx] = changedValue;

    var exact = findVariant(variants, test);
    if (exact) return exact;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var vOpts = [v.option1 || null, v.option2 || null, v.option3 || null];
      if (vOpts[changedIdx] === changedValue && v.available) return v;
    }

    for (var k = 0; k < variants.length; k++) {
      var u = variants[k];
      var uOpts = [u.option1 || null, u.option2 || null, u.option3 || null];
      if (uOpts[changedIdx] === changedValue) return u;
    }
    return null;
  }

  /**
   * @param {HTMLElement|null} el
   * @returns {boolean}
   */
  function hasActiveClass(el) {
    if (!el) return false;
    return /(?:^|\s)\S*--active(?:\s|$)/.test(el.className || '');
  }

  /**
   * Read currently active option values from a DOM scope.
   * Looks for `[data-option-index]` groups and finds the active button in each.
   *
   * @param {HTMLElement} root
   * @returns {Array<string|null>}
   */
  function readSelections(root) {
    /** @type {Array<string|null>} */
    var opts = [null, null, null];
    root.querySelectorAll('[data-option-index]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      /** @type {HTMLElement | null} */
      var active = null;
      group.querySelectorAll('[data-option-value]').forEach(function (btn) {
        if (!active && hasActiveClass(/** @type {HTMLElement} */ (btn))) {
          active = /** @type {HTMLElement} */ (btn);
        }
      });
      if (active) opts[idx] = /** @type {HTMLElement} */ (active).getAttribute('data-option-value');
    });
    return opts;
  }

  /**
   * Toggle a `--unavailable` modifier on each option button based on whether a
   * variant with that value and the rest of `currentOpts` is purchasable.
   *
   * @param {HTMLElement} root
   * @param {Array<ProductVariant>} variants
   * @param {Array<string|null>} currentOpts
   */
  function recomputeAvailability(root, variants, currentOpts) {
    root.querySelectorAll('[data-option-index]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      group.querySelectorAll('[data-option-value]').forEach(function (el) {
        var value = el.getAttribute('data-option-value');
        var available = false;

        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var vOpts = [v.option1 || null, v.option2 || null, v.option3 || null];
          if (vOpts[idx] !== value) continue;

          var otherMatch = true;
          for (var j = 0; j < 3; j++) {
            if (j === idx) continue;
            if (currentOpts[j] && vOpts[j] !== currentOpts[j]) {
              otherMatch = false;
              break;
            }
          }
          if (otherMatch && v.available) {
            available = true;
            break;
          }
        }

        var classes = (el.className || '').split(/\s+/);
        for (var k = 0; k < classes.length; k++) {
          var c = classes[k];
          if (/--unavailable$/.test(c)) {
            el.classList.toggle(c, !available);
          }
        }
        var prefix = baseUnavailablePrefix(el);
        if (prefix) el.classList.toggle(prefix + '--unavailable', !available);
      });
    });
  }

  /**
   * Determine the BEM base class an option button uses so we can derive its
   * `--unavailable` modifier. Falls back to known atomberg prefixes when no
   * existing modifier is present yet.
   *
   * @param {Element} el
   * @returns {string | null}
   */
  function baseUnavailablePrefix(el) {
    var KNOWN = ['atomberg-swatch', 'atomberg-chip', 'atomberg-color-tile'];
    for (var i = 0; i < KNOWN.length; i++) {
      if (el.classList.contains(KNOWN[i])) return KNOWN[i];
    }
    return null;
  }

  /**
   * Format a price for display, matching the Liquid `money` filter shape used in
   * server-rendered markup. When `symbol` is blank, defers to `Shopify.formatMoney`
   * using the shop's configured `money_format`. Otherwise prepends the supplied
   * symbol and formats the amount with Indian (`en-IN`) digit grouping.
   *
   * @param {number} cents
   * @param {string} symbol
   * @param {boolean} showDecimals
   * @returns {string}
   */
  function formatPrice(cents, symbol, showDecimals) {
    if (!symbol) {
      var sf = /** @type {any} */ (window).Shopify;
      if (sf && typeof sf.formatMoney === 'function') {
        return sf.formatMoney(cents, sf.money_format || '');
      }
    }
    var amount = cents / 100;
    var formatted;
    if (showDecimals) {
      formatted = amount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      formatted = Math.round(amount).toLocaleString('en-IN');
    }
    return (symbol || '') + formatted;
  }

  /**
   * @param {ProductVariant} variant
   * @returns {number}
   */
  function discountPercent(variant) {
    var cmp = variant.compare_at_price || 0;
    if (cmp <= variant.price) return 0;
    return Math.round(((cmp - variant.price) / cmp) * 100);
  }

  w.AtombergVariantSelector = {
    findVariant: findVariant,
    findBestAvailable: findBestAvailable,
    readSelections: readSelections,
    recomputeAvailability: recomputeAvailability,
    formatPrice: formatPrice,
    discountPercent: discountPercent,
  };
})();
